// src/components/charts/assistant/theme.ts
/**
 * Assistant-specific chart theme and helpers.
 * 
 * Built on top of the unified theme system but with assistant-specific
 * styling for dark card backgrounds and custom tooltip formatting.
 */

import { 
  CHART_COLORS,
  getSemanticColors,
  getSeriesPalette,
} from "@/utils/charts/theme";
import { formatCurrencyShort, formatPercent } from "@/utils/charts/formatters";

// ─────────────────────────────────────────────────────────────────────────────
// Assistant Theme
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Assistant-specific theme built on unified CHART_COLORS.
 * Uses slightly different text opacity for the dark assistant UI.
 */
export const ASSISTANT_CHART_THEME = {
  // Semantic colors (from unified theme - muted variant for dark UI)
  semantic: getSemanticColors('muted'),
  // Treemap uses deep variant for large areas
  treemap: getSemanticColors('deep'),
  // Line chart colors (assistant palette from unified system)
  lineColors: getSeriesPalette('assistant'),
  // Text colors (assistant-specific opacity for dark cards)
  text: {
    primary: "rgba(255,255,255,0.9)",
    secondary: "rgba(255,255,255,0.6)",
    muted: "rgba(255,255,255,0.4)",
  },
  // Grid (using unified base, slightly adjusted for assistant)
  grid: {
    line: "rgba(255,255,255,0.06)",
  },
  // Tooltip styling
  tooltip: {
    background: "rgba(20,20,28,0.95)",
    border: "rgba(255,255,255,0.1)",
  },
  background: CHART_COLORS.background,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Default Chart Height
// ─────────────────────────────────────────────────────────────────────────────

export const ASSISTANT_CHART_HEIGHT = 300;

// ─────────────────────────────────────────────────────────────────────────────
// Formatter Types and Helpers
// ─────────────────────────────────────────────────────────────────────────────

export interface FormatterOptions {
  unit?: string;
  /** When true, values are decimals (0.05 = 5%) that need multiplication */
  isChangeMetric?: boolean;
}

/**
 * Get a value formatter based on options.
 */
export function getFormatter(options: FormatterOptions = {}): (v: number) => string {
  const { unit, isChangeMetric } = options;
  
  // Change metrics: API returns decimals (0.05 = 5%), multiply by 100 for display
  if (isChangeMetric) {
    return (v) => {
      const pct = v * 100;
      const sign = pct >= 0 ? "+" : "";
      return `${sign}${pct.toFixed(2)}%`;
    };
  }
  
  if (!unit) return (v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (unit === "$" || unit.toLowerCase() === "usd") return formatCurrencyShort;
  if (unit === "%") return (v) => formatPercent(v, { decimals: 2 });
  
  // Handle unit suffix
  return (v) => `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chart Spec Helpers (used by ChartRenderer)
// ─────────────────────────────────────────────────────────────────────────────

import type { ChartSpec, MetricSpec } from "@/types/assistant";
import type { MetricInfo } from "@/types/metrics";

/**
 * Derive format options from metric spec and catalog
 */
export function deriveFormatOptions(
  metricSpec: MetricSpec | undefined,
  catalogMap: Record<string, MetricInfo>,
  axisConfig?: { unit?: string; format?: string }
): FormatterOptions {
  if (axisConfig?.unit) {
    return { unit: axisConfig.unit };
  }
  
  if (metricSpec?.transform === "change") {
    return { isChangeMetric: true };
  }
  
  if (metricSpec?.key) {
    const catalogEntry = catalogMap[metricSpec.key];
    if (catalogEntry?.unit) {
      return { unit: catalogEntry.unit };
    }
  }
  
  return {};
}

export type StackingMode = "none" | "stacked" | "100_percent";
export type ChartStyle = "line" | "area" | "bar";

/**
 * Get stacking mode from chart spec
 */
export function getStackingMode(spec: ChartSpec): StackingMode {
  if (spec.stacking) return spec.stacking;
  if (spec.stacked) return "stacked";
  return "none";
}

/**
 * Get chart style from spec
 */
export function getChartStyle(spec: ChartSpec): ChartStyle {
  if (spec.style) return spec.style;
  return "line";
}

/**
 * Derive trend from change value
 */
export function deriveTrend(change: number | undefined): "up" | "down" | "neutral" {
  if (change === undefined || change === null) return "neutral";
  if (change > 0) return "up";
  if (change < 0) return "down";
  return "neutral";
}
